import { useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableSortLabel,
  Paper,
  Typography,
  Fab,
  Tooltip,
  Box,
  Card,
  CardContent,
  Chip,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import AddIcon from '@mui/icons-material/Add';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';

import { goTo } from '../../shared/utils';
import { mapTransactionType } from '../../shared/domainMaps';
import { updateCategoryOrder, loadCategory } from '../../redux/categorySlice';
import Filter from './filter';

const StyledPaper = styled(Paper)(({ theme }) => ({
  width: '100%',
  overflowX: 'auto',
  marginTop: theme.spacing(3),
  borderRadius: '16px',
  boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
  [theme.breakpoints.down('sm')]: {
    display: 'none',
  },
}));

const MobileContainer = styled(Box)(({ theme }) => ({
  display: 'none',
  [theme.breakpoints.down('sm')]: {
    display: 'block',
    marginTop: theme.spacing(2),
  },
}));

const CategoryCard = styled(Card)(({ theme }) => ({
  marginBottom: theme.spacing(2),
  borderRadius: '16px',
  boxShadow: '0 2px 12px rgba(0, 0, 0, 0.08)',
  transition: 'all 0.3s ease',
  '&:hover': {
    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.12)',
    transform: 'translateY(-2px)',
  },
  '&:active': {
    transform: 'scale(0.98)',
  },
}));

const CategoryCardContent = styled(CardContent)(({ theme }) => ({
  padding: theme.spacing(2),
  '&:last-child': {
    paddingBottom: theme.spacing(2),
  },
}));

const CardHeader = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'flex-start',
  marginBottom: theme.spacing(1.5),
}));

const PriorityChip = styled(Chip)(({ theme }) => ({
  backgroundColor: 'rgba(0, 98, 105, 0.1)',
  color: 'rgb(0,98,105)',
  fontWeight: 700,
  fontSize: '1.125rem',
  padding: theme.spacing(1.5, 1),
  height: 'auto',
  borderRadius: '12px',
}));

const TypeChip = styled(Chip)(({ theme, typevalue }) => ({
  fontWeight: 600,
  borderRadius: '8px',
  fontSize: '0.813rem',
  backgroundColor: 
    typevalue === 'credit' ? 'rgba(76, 175, 80, 0.1)' :
    typevalue === 'credit-transfer' ? 'rgba(33, 150, 243, 0.1)' :
    typevalue === 'debit' ? 'rgba(244, 67, 54, 0.1)' :
    'rgba(255, 152, 0, 0.1)',
  color: 
    typevalue === 'credit' ? theme.palette.success.dark :
    typevalue === 'credit-transfer' ? theme.palette.info.dark :
    typevalue === 'debit' ? theme.palette.error.dark :
    theme.palette.warning.dark,
  '& .MuiChip-icon': {
    color: 'inherit',
  },
}));

const StyledTable = styled(Table)({
  minWidth: 700,
});

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  cursor: 'pointer',
  transition: 'background-color 0.2s ease',
  '&:hover': {
    backgroundColor: 'rgba(0, 98, 105, 0.04)',
  },
}));

const StyledTableHead = styled(TableHead)(({ theme }) => ({
  backgroundColor: 'rgba(0, 98, 105, 0.05)',
  '& .MuiTableCell-head': {
    fontWeight: 700,
    color: theme.palette.text.primary,
    fontSize: '0.875rem',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  },
}));

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  fontSize: '0.938rem',
  fontWeight: 500,
  borderBottom: '1px solid rgba(0, 0, 0, 0.06)',
}));

const StyledFab = styled(Fab)(({ theme }) => ({
  zIndex: '1000',
  boxShadow: '0 8px 24px rgba(0, 98, 105, 0.4)',
  '&:hover': {
    transform: 'scale(1.05)',
    boxShadow: '0 12px 32px rgba(0, 98, 105, 0.5)',
  },
  transition: 'all 0.3s ease',
  [theme.breakpoints.down('sm')]: {
    position: 'fixed',
    bottom: theme.spacing(2),
    right: theme.spacing(2),
  },
  [theme.breakpoints.up('sm')]: {
    marginLeft: theme.spacing(1),
  },
}));

const HeaderContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  marginBottom: theme.spacing(3),
  gap: theme.spacing(2),
  position: 'relative',
  [theme.breakpoints.down('sm')]: {
    flexDirection: 'column',
    alignItems: 'flex-start',
  },
}));

const HeaderLeft = styled(Box)(({ theme }) => ({
  display: 'flex',
  gap: theme.spacing(2),
  flex: 1,
  minWidth: 0,
  flexDirection: 'column',
  alignItems: 'flex-start',
  [theme.breakpoints.down('sm')]: {
    flexDirection: 'column',
    alignItems: 'flex-start',
    width: '100%',
  },
}));

const HeaderRight = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
  [theme.breakpoints.down('sm')]: {
    display: 'none',
  },
}));

const VisuallyHidden = styled('span')({
  border: 0,
  clip: 'rect(0 0 0 0)',
  height: 1,
  margin: -1,
  overflow: 'hidden',
  padding: 0,
  position: 'absolute',
  top: 20,
  width: 1,
});

const handleOrder = (updateOrder, event, order, selectedOrder) => {
  event.preventDefault();
  const isAsc = order.by === selectedOrder && order.direction === 'asc';
  var direction = isAsc ? 'desc' : 'asc';
  updateOrder({ by: selectedOrder, direction });
};

function List() {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  // Redux state selectors
  const items = useSelector((state) => state.category.items);
  const shouldBeUpdated = useSelector(
    (state) => state.category.shouldBeUpdated
  );
  const order = useSelector((state) => state.category.order);

  const updateOrder = (order) => dispatch(updateCategoryOrder(order));

  useEffect(() => {
    if (shouldBeUpdated) {
      dispatch(loadCategory());
    }
  }, [shouldBeUpdated, dispatch]);

  const getTypeLabel = (type) => {
    switch(type) {
      case 'credit': return 'Crédito';
      case 'credit-transfer': return 'Transferência de Crédito';
      case 'debit': return 'Débito';
      case 'debit-transfer': return 'Transferência de Débito';
      default: return type;
    }
  };

  const getTypeIcon = (type) => {
    if (type === 'credit') return <TrendingUpIcon />;
    if (type === 'debit') return <TrendingDownIcon />;
    return <SwapHorizIcon />;
  };

  const renderMobileCards = () => (
    <MobileContainer>
      {(items || []).map((item) => (
        <CategoryCard 
          key={item.id}
          onClick={() => goTo(navigate, `categories/edit/${item.id}`)}
        >
          <CategoryCardContent>
            <CardHeader>
              <Box sx={{ flex: 1 }}>
                <Typography variant="subtitle1" fontWeight={700} gutterBottom>
                  {item.name}
                </Typography>
                <TypeChip 
                  icon={getTypeIcon(item.type)}
                  label={getTypeLabel(item.type)} 
                  size="small"
                  typevalue={item.type}
                />
              </Box>
              <PriorityChip 
                label={item.priority}
              />
            </CardHeader>
          </CategoryCardContent>
        </CategoryCard>
      ))}
    </MobileContainer>
  );

  return (
    <>
      {/* Mobile FAB */}
      <Box sx={{ display: { xs: 'block', sm: 'none' } }}>
        <Tooltip title="Nova Categoria" aria-label="Nova Categoria">
          <StyledFab
            color="primary"
            aria-label="Add"
            onClick={() => goTo(navigate, 'categories/new')}
          >
            <AddIcon />
          </StyledFab>
        </Tooltip>
      </Box>

      {/* Header with Title, Filters, and Action Buttons */}
      <HeaderContainer>
        <HeaderLeft>
          <Typography variant="h4" component="h2" sx={{ 
            fontWeight: 700,
            color: '#1a1a1a',
            margin: 0,
          }}>
            Categorias
          </Typography>
          <Filter></Filter>
        </HeaderLeft>
        
        <HeaderRight>
          <Tooltip title="Nova Categoria" aria-label="Nova Categoria">
            <StyledFab
              color="primary"
              aria-label="Add"
              onClick={() => goTo(navigate, 'categories/new')}
              size="medium"
            >
              <AddIcon />
            </StyledFab>
          </Tooltip>
        </HeaderRight>
      </HeaderContainer>

      {/* Mobile Card View */}
      {renderMobileCards()}
      
      {/* Desktop Table View */}
      <StyledPaper>
        <StyledTable stickyHeader>
          <StyledTableHead>
            <TableRow>
              <StyledTableCell
                sortDirection={order.by === 'name' ? order.direction : false}
              >
                <TableSortLabel
                  active={order.by === 'name'}
                  direction={order.direction}
                  onClick={(event) =>
                    handleOrder(updateOrder, event, order, 'name')
                  }
                >
                  Nome
                  {order.by === 'name' ? (
                    <VisuallyHidden>
                      {order.direction === 'desc'
                        ? 'sorted descending'
                        : 'sorted ascending'}
                    </VisuallyHidden>
                  ) : null}
                </TableSortLabel>
              </StyledTableCell>
              <StyledTableCell
                align="left"
                sortDirection={order.by === 'type' ? order.direction : false}
              >
                <TableSortLabel
                  active={order.by === 'type'}
                  direction={order.direction}
                  onClick={(event) =>
                    handleOrder(updateOrder, event, order, 'type')
                  }
                >
                  Tipo
                  {order.by === 'type' ? (
                    <VisuallyHidden>
                      {order.direction === 'desc'
                        ? 'sorted descending'
                        : 'sorted ascending'}
                    </VisuallyHidden>
                  ) : null}
                </TableSortLabel>
              </StyledTableCell>
              <StyledTableCell
                align="right"
                sortDirection={order.by === 'name' ? order.direction : false}
              >
                <TableSortLabel
                  active={order.by === 'priority'}
                  direction={order.direction}
                  onClick={(event) =>
                    handleOrder(updateOrder, event, order, 'priority')
                  }
                >
                  Prioridade
                  {order.by === 'priority' ? (
                    <VisuallyHidden>
                      {order.direction === 'desc'
                        ? 'sorted descending'
                        : 'sorted ascending'}
                    </VisuallyHidden>
                  ) : null}
                </TableSortLabel>
              </StyledTableCell>
              <StyledTableCell
                align="right"
                sortDirection={order.by === 'active' ? order.direction : false}
              >
                <TableSortLabel
                  active={order.by === 'active'}
                  direction={order.direction}
                  onClick={(event) =>
                    handleOrder(updateOrder, event, order, 'active')
                  }
                >
                  Ativo
                  {order.by === 'active' ? (
                    <VisuallyHidden>
                      {order.direction === 'desc'
                        ? 'sorted descending'
                        : 'sorted ascending'}
                    </VisuallyHidden>
                  ) : null}
                </TableSortLabel>
              </StyledTableCell>
              <StyledTableCell
                align="right"
                sortDirection={order.by === 'goal' ? order.direction : false}
              >
                <TableSortLabel
                  active={order.by === 'goal'}
                  direction={order.direction}
                  onClick={(event) =>
                    handleOrder(updateOrder, event, order, 'goal')
                  }
                >
                  Meta Mensal
                  {order.by === 'goal' ? (
                    <VisuallyHidden>
                      {order.direction === 'desc'
                        ? 'sorted descending'
                        : 'sorted ascending'}
                    </VisuallyHidden>
                  ) : null}
                </TableSortLabel>
              </StyledTableCell>
              <StyledTableCell
                align="right"
                sortDirection={order.by === 'userId' ? order.direction : false}
              >
                <TableSortLabel
                  active={order.by === 'group'}
                  direction={order.direction}
                  onClick={(event) =>
                    handleOrder(updateOrder, event, order, 'group')
                  }
                >
                  Agrupamento
                  {order.by === 'group' ? (
                    <VisuallyHidden>
                      {order.direction === 'desc'
                        ? 'sorted descending'
                        : 'sorted ascending'}
                    </VisuallyHidden>
                  ) : null}
                </TableSortLabel>
              </StyledTableCell>
            </TableRow>
          </StyledTableHead>
          <TableBody>
            {(items || []).map((item) => (
              <StyledTableRow
                hover
                key={item.id}
                onClick={() => goTo(navigate, `categories/edit/${item.id}`)}
              >
                <StyledTableCell component="th" scope="row">
                  {item.name}
                </StyledTableCell>
                <StyledTableCell align="left">
                  {mapTransactionType(item.type)}
                </StyledTableCell>
                <StyledTableCell align="right">{item.priority}</StyledTableCell>
                <StyledTableCell align="right">
                  {item.active ? 'Sim' : 'Não'}
                </StyledTableCell>
                <StyledTableCell align="right">R$ {item.goal.toFixed(2)}</StyledTableCell>
                <StyledTableCell align="right">
                  {!!item.groupId ? item.group.name : ''}
                </StyledTableCell>
              </StyledTableRow>
            ))}
          </TableBody>
        </StyledTable>
      </StyledPaper>
    </>
  );
}

export default List;
